import React, { useState, useEffect } from 'react';
import styles from './styles.module.scss';
import { gql } from '../../utils/graphql';
import { useModal } from '../../context/ModalContext';

interface PrizesProps {
	ambassador: any;
	token: string;
	refreshData: () => void;
}

interface InviteEvent {
	id: string;
	name: string;
	city?: string;
	date?: string;
}

const Prizes: React.FC<PrizesProps> = ({ ambassador, token, refreshData }) => {
	const { confirm, showAlert } = useModal();
	const [loading, setLoading] = useState(false);
	const [msg, setMsg] = useState('');
	const [prizes, setPrizes] = useState<any[]>([]);
	const [inviteEvents, setInviteEvents] = useState<InviteEvent[]>([]);
	const [showEventPicker, setShowEventPicker] = useState(false);
	const [pendingPrize, setPendingPrize] = useState<any>(null);
	const [selectedEventId, setSelectedEventId] = useState<string>('');

	useEffect(() => {
		const fetchPrizes = async () => {
			const query = `
				query {
					prizes {
						id
						name
						description
						value
						isActive
						isInvite
						redemableOnPortal
					}
				}
			`;
			try {
				const res = await gql(query, {}, token);
				if (res.data?.data?.prizes) {
					// Only show active prizes
					setPrizes(res.data.data.prizes.filter((p: any) => p.isActive));
				}
			} catch (e) {
				console.error("Failed to fetch prizes", e);
			}
		};
		fetchPrizes();
	}, [token]);

	// Fetch ambassador events that have inviteId configured
	useEffect(() => {
		if (!ambassador?.events) return;
		const eventsWithInvite = (ambassador.events || []).filter((e: any) => e.inviteId);
		setInviteEvents(eventsWithInvite.map((e: any) => ({
			id: e.id || e._id,
			name: e.name,
			city: e.city,
			date: e.date,
		})));
	}, [ambassador?.events]);

	const executeRedeem = async (prize: any, eventId?: string) => {
		setLoading(true);
		setMsg('');

		try {
			const query = `
				mutation RedeemPrize($prizeName: String!, $cost: Float!, $eventId: ID) {
					redeemPrize(prizeName: $prizeName, cost: $cost, eventId: $eventId) {
						id
						points
						redeemedPrizes {
							name
							date
							isValidated
						}
					}
				}
			`;

			const variables: any = { prizeName: prize.name, cost: prize.value || 0 };
			if (eventId) variables.eventId = eventId;

			const response = await gql(query, variables, token);

			if (response.data?.errors) {
				throw new Error(response.data.errors[0].message);
			}

			setMsg(`Ai revendicat cu succes ${prize.name}!`);
			refreshData();
		} catch (err: any) {
			console.error(err);
			setMsg(`Eroare: ${err.message}`);
			showAlert('Eroare', err.message);
		} finally {
			setLoading(false);
			setShowEventPicker(false);
			setPendingPrize(null);
			setSelectedEventId('');
		}
	};

	const handleRedeem = async (prize: any) => {
		const cost = prize.value || 0;
		const pendingPoints = (ambassador.redeemedPrizes || [])
			.filter((p: any) => !p.isValidated)
			.reduce((sum: number, p: any) => sum + (p.cost || 0), 0);
		const balance = (ambassador.points || 0) - (ambassador.spentPoints || 0) - pendingPoints;
		if (balance < cost) return;

		// If this is an invite prize and there are multiple events, show event picker
		if (prize.isInvite && inviteEvents.length > 1) {
			setPendingPrize(prize);
			setShowEventPicker(true);
			return;
		}

		const ok = await confirm({
			title: 'Confirmare Revendicare',
			content: `Ești sigur că vrei să revendici ${prize.name} pentru ${cost} puncte?`
		});

		if (ok) {
			// For invite prizes with exactly 1 event, pass it automatically
			const autoEventId = prize.isInvite && inviteEvents.length === 1 ? inviteEvents[0].id : undefined;
			await executeRedeem(prize, autoEventId);
		}
	};

	const handleEventPickerConfirm = async () => {
		if (!selectedEventId || !pendingPrize) return;

		const cost = pendingPrize.value || 0;
		const selectedEvent = inviteEvents.find(e => e.id === selectedEventId);
		const eventLabel = selectedEvent ? (selectedEvent.city || selectedEvent.name) : '';

		const ok = await confirm({
			title: 'Confirmare Revendicare',
			content: `Ești sigur că vrei să revendici ${pendingPrize.name} pentru ${cost} puncte? Invitația va fi trimisă pentru evenimentul: ${eventLabel}`
		});

		if (ok) {
			await executeRedeem(pendingPrize, selectedEventId);
		}
	};

	return (
		<div>
			{/* Balance badge */}
			<div className={styles.balanceBadge}>
				{(() => {
					const pendingPoints = (ambassador.redeemedPrizes || [])
						.filter((p: any) => !p.isValidated)
						.reduce((sum: number, p: any) => sum + (p.cost || 0), 0);
					const available = (ambassador.points || 0) - (ambassador.spentPoints || 0) - pendingPoints;
					return `Disponibil: ${available} ${available === 1 ? 'punct' : 'puncte'}`;
				})()}
			</div>

			{/* Feedback message */}
			{msg && (
				<div className={`${styles.msgBox} ${msg.includes('Error') || msg.includes('Eroare') ? styles.msgError : styles.msgSuccess}`}>
					{msg}
				</div>
			)}

			{/* Event Picker Modal for invite prizes */}
			{showEventPicker && pendingPrize && (
				<div className={styles.eventPickerOverlay} onClick={() => { setShowEventPicker(false); setPendingPrize(null); }}>
					<div className={styles.eventPickerModal} onClick={e => e.stopPropagation()}>
						<h3>Alege evenimentul pentru invitație</h3>
						<p className={styles.eventPickerDesc}>
							Ai mai multe evenimente disponibile. Selectează pentru care vrei să primești invitația:
						</p>
						<div className={styles.eventPickerList}>
							{inviteEvents.map(event => (
								<label
									key={event.id}
									className={`${styles.eventPickerItem} ${selectedEventId === event.id ? styles.eventPickerItemSelected : ''}`}
								>
									<input
										type="radio"
										name="inviteEvent"
										value={event.id}
										checked={selectedEventId === event.id}
										onChange={() => setSelectedEventId(event.id)}
									/>
									<div className={styles.eventPickerInfo}>
										<span className={styles.eventPickerName}>{event.city || event.name}</span>
										{event.date && (
											<span className={styles.eventPickerDate}>
												{new Date(event.date).toLocaleDateString('ro-RO', { day: 'numeric', month: 'long', year: 'numeric' })}
											</span>
										)}
									</div>
								</label>
							))}
						</div>
						<div className={styles.eventPickerActions}>
							<button
								className={styles.buttonSecondary}
								onClick={() => { setShowEventPicker(false); setPendingPrize(null); }}
							>
								Anulează
							</button>
							<button
								className={styles.button}
								disabled={!selectedEventId || loading}
								onClick={handleEventPickerConfirm}
							>
								{loading ? '...' : 'Confirmă'}
							</button>
						</div>
					</div>
				</div>
			)}

			{/* Prize cards grid */}
			<div className={styles.grid}>
				{prizes.length === 0 ? (
					<div className={styles.emptyState} style={{ gridColumn: '1 / -1' }}>
						<p>Momentan nu există premii disponibile.</p>
					</div>
				) : (
					prizes.map((prize, idx) => {
						const cost = prize.value || 0;
						const isRedeemable = prize.redemableOnPortal !== false;
						const pendingPoints = (ambassador.redeemedPrizes || [])
							.filter((p: any) => !p.isValidated)
							.reduce((sum: number, p: any) => sum + (p.cost || 0), 0);
						const canAfford = ((ambassador.points || 0) - (ambassador.spentPoints || 0) - pendingPoints) >= cost;
						const isInvitePrize = prize.isInvite === true;
						return (
							<div key={idx} className={`${styles.prizeCard} ${isRedeemable && !canAfford ? styles.dimmed : ''}`}>
								<div className={styles.prizeName}>
									{prize.name}
									{isInvitePrize && <span className={styles.inviteBadge}>Invitație</span>}
								</div>
								{prize.description?.trim() && (
									<div className={styles.prizeDesc}>{prize.description}</div>
								)}
								{isRedeemable && (
									<>
										<div className={styles.prizeCost}>
											{cost} <span>{cost === 1 ? 'punct' : 'puncte'}</span>
										</div>
										<button
											className={styles.button}
											disabled={!canAfford || loading}
											onClick={() => handleRedeem(prize)}
										>
											{loading ? '...' : canAfford ? 'Revendică' : 'Puncte insuficiente'}
										</button>
									</>
								)}
							</div>
						);
					})
				)}
			</div>

			{/* Redemption History */}
			<div className={styles.card} style={{ marginTop: '2rem' }}>
				<div className={styles.cardTitle}>Istoric Revendicări</div>
				{ambassador.redeemedPrizes && ambassador.redeemedPrizes.length > 0 ? (
					<div>
						{ambassador.redeemedPrizes.slice().reverse().map((item: any, i: number) => (
							<div key={i} className={styles.historyItem}>
								<div className={styles.historyText}>
									Revendicat <b>{item.name}</b> pentru {item.cost} {item.cost === 1 ? 'punct' : 'puncte'} pe {new Date(Number(item.date)).toLocaleDateString()}
								</div>
								<span className={`${styles.badge} ${item.isValidated ? styles.badgeSuccess : styles.badgePending}`}>
									{item.isValidated ? 'Validat' : 'În așteptare'}
								</span>
							</div>
						))}
					</div>
				) : (
					<div className={styles.emptyState}>
						<p>Nu există încă un istoric de revendicare.</p>
					</div>
				)}
			</div>

		</div>
	);
};

export default Prizes;
